import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Input } from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import { requestResetPassword, clearForgotPasswordState } from "#root/redux/auth/actions";
import MetaData from "../MetaData";
import "./styles.scss";
import { showToast } from "#root/components/GlobalToaster";
import { useOrganizationRequest } from "#root/queries/auth";
import { TenantSelectionDialog } from "#root/components/TenantSelectionDialog";

const emailRegex = /\S+@\S+\.\S+/;

const ForgotPassword: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { loading, error, resetRequestSuccess } = useSelector((state: any) => state.auth);

  const [email, setEmail] = useState("");
  const [inlineMessage, setInlineMessage] = useState({ message: "", type: "" });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  const {
    data: tenantsData,
    error: tenantsError,
    isLoading: tenantsLoading,
    refetch: fetchOrganizations,
  } = useOrganizationRequest({ email }, { enabled: false });

  const tenants = tenantsData?.items || [];

  useEffect(() => {
    setInlineMessage({ message: "", type: "" });
    dispatch(clearForgotPasswordState());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (tenantsError || error) {
      setRequestSubmitted(false);
      setIsSubmitted(false);
      showToast(`${tenantsError?.message || error}`, "error", "top");
    }
  }, [tenantsError, error]);

  useEffect(() => {
    if (resetRequestSuccess) {
      setInlineMessage({
        message: "Instructions to reset your password have been sent to your email",
        type: "success",
      });
    }
  }, [resetRequestSuccess]);

  useEffect(() => {
    if (!isSubmitted || tenantsLoading || requestSubmitted || !tenants) return;
    if (tenants.length <= 1) {
      handleRequestResetPassword(tenants[0]?.id || '');
    } else {
      setShowTenantModal(true);
    }
  }, [tenants, isSubmitted, tenantsLoading, requestSubmitted]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !emailRegex.test(email)) {
      setInlineMessage({ message: "Please enter a valid email", type: "error" });
      return;
    }
    setInlineMessage({ message: "", type: "" });
    setIsSubmitted(true);
    setSelectedTenantId("");
    fetchOrganizations();
  };

  const handleRequestResetPassword = (tenantId: string) => {
    setRequestSubmitted(true);
    dispatch(requestResetPassword({ email, tenantId }));
  };

  const handleTenantSelect = () => {
    if (!selectedTenantId || requestSubmitted) return;
    setShowTenantModal(false);
    handleRequestResetPassword(selectedTenantId);
  };

  const closeTenantDialog = () => {
    setShowTenantModal(false);
    setIsSubmitted(false);
  };

  return (
    <>
      <MetaData title="Forgot Password" description="Request a password reset link" />
      <div className="forgot-password-page">
        <div className="forgot-password-page__left">
          <div className="forgot-password-page__form-container">
            <div className="forgot-password-page__form-header">
              <img src="/images/emc-logo.svg" alt="EMC Logo" className="forgot-password-page__logo" />
            </div>
            <h1 className="forgot-password-page__title">Forgot Your Password?</h1>
            <p>
              Enter the email address to which you would like your password reset instructions sent.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="form-field">
                <label htmlFor="email">Enter Email Address</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="youremail@emc.com"
                  required
                  value={email}
                  onChange={handleEmailChange}
                />
              </div>
              {inlineMessage.message && (
                <div className={`inline-message ${inlineMessage.type}`}>
                  {inlineMessage.message}
                </div>
              )}
              <button type="submit" disabled={loading || tenantsLoading}>
                {loading || tenantsLoading ? "Please wait..." : "Request Reset Password"}
              </button>
              <div className="back-to-login">
                <Link
                  to="/login"
                  onClick={() => {
                    setInlineMessage({ message: "", type: "" });
                    dispatch(clearForgotPasswordState());
                  }}
                >
                  Back To Login
                </Link>
              </div>
            </form>
          </div>
          <div className="forgot-password-page__footer">
            <p className="forgot-password-page__copyright">&copy; {new Date().getFullYear()} EMC</p>
          </div>
        </div>
        <div className="forgot-password-page__right" />
        <TenantSelectionDialog
          open={showTenantModal}
          onOpenChange={closeTenantDialog}
          title="Select the Organization"
          primaryButtonText="Reset Password for Organization"
          onPrimaryButtonClick={handleTenantSelect}
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={closeTenantDialog}
          isPrimaryButtonLoading={loading}
          isPrimaryButtonDisabled={!selectedTenantId || tenantsLoading}
          isSecondaryButtonDisabled={loading}
          selectedTenantId={selectedTenantId}
          setSelectedTenantId={setSelectedTenantId}
          tenants={tenants || []}
        />
      </div>
    </>
  );
};

export default ForgotPassword;
